import { ReactNode, useCallback, useMemo } from 'react';

import { Addendum, api } from '@/api';
import { useIsDDFeatureFlagEnabled } from '@/core/data-dog';
import { useStudyGuard } from '@/shared/guard';
import type { AddendumFieldCapabilityResult, AddendumFieldId } from '@/shared/model';
import { resolveAddendumFieldCapability } from '@/shared/util';
import { useStudyModeStore } from '@/store';

import { AddendumContext } from './addendum.context';
import type { AddendumContextState } from './addendum.model';

const EMPTY_ADDENDA: Addendum[] = [];

interface AddendumProviderProps {
  children: ReactNode;
}

export function AddendumProvider({ children }: AddendumProviderProps) {
  const { report, studyId } = useStudyGuard();
  const isAddendumEnabled = useIsDDFeatureFlagEnabled('FCT_ADDENDUM');
  const isAiAddendumEnabled = useIsDDFeatureFlagEnabled('FCT_AI_ADDENDUM');

  const studyMode = useStudyModeStore((state) => state.studyMode);
  const isAddendumMode = useStudyModeStore((state) => state.isAddendumMode);
  const isStudyModeResolving = useStudyModeStore((state) => state.isStudyModeResolving);

  const shouldFetchAddendumData = isAddendumEnabled && Boolean(report?.isReleased);

  const { data: sessionData, isLoading: isSessionLoading } =
    api.fct.addendumSession.query.useGetAddendumSession(
      { payload: { studyId } },
      { enabled: shouldFetchAddendumData },
    );

  const { data: addenda = EMPTY_ADDENDA, isLoading: isAddendaLoading } =
    api.fct.addendum.query.useGetAddenda(
      { payload: { studyId } },
      { enabled: shouldFetchAddendumData },
    );

  const getFieldCapability = useCallback(
    (fieldId: AddendumFieldId): AddendumFieldCapabilityResult =>
      resolveAddendumFieldCapability({ fieldId, studyMode }),
    [studyMode],
  );

  const isFieldEditable = useCallback(
    (fieldId: AddendumFieldId): boolean => {
      if (!isAddendumEnabled) {
        return true;
      }

      if (isStudyModeResolving) {
        return false;
      }

      return getFieldCapability(fieldId).isEditable;
    },
    [getFieldCapability, isAddendumEnabled, isStudyModeResolving],
  );

  const value = useMemo<AddendumContextState>(
    () => ({
      activeSession: sessionData?.activeSession ?? null,
      addenda,
      canEnterAddendum: sessionData?.canEnterAddendum ?? false,
      getFieldCapability,
      isAddendaLoading,
      isAddendumEnabled,
      isAddendumMode,
      isAiAddendumEnabled,
      isCurrentUserSession: sessionData?.isCurrentUserSession ?? false,
      isFieldEditable,
      isSessionLoading: shouldFetchAddendumData ? isSessionLoading : false,
      isStudyModeResolving,
      permissionReason: sessionData?.permissionReason ?? null,
    }),
    [
      addenda,
      getFieldCapability,
      isAddendaLoading,
      isAddendumEnabled,
      isAiAddendumEnabled,
      isAddendumMode,
      isFieldEditable,
      isSessionLoading,
      isStudyModeResolving,
      sessionData?.activeSession,
      sessionData?.canEnterAddendum,
      sessionData?.isCurrentUserSession,
      sessionData?.permissionReason,
      shouldFetchAddendumData,
    ],
  );

  return <AddendumContext value={value}>{children}</AddendumContext>;
}
